import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analyticsService } from '../services/api';
import UserAnalyticsService from '../services/userAnalytics/userAnalytics.service';

const userAnalyticsService = UserAnalyticsService.getInstance();

export const userAnalytics = (userId: string) => {
  const location = useLocation();
  const prevPathRef = useRef<string>(location.pathname);
  const entryTimeRef = useRef<number>(performance.now());
  const pageLoadStartRef = useRef<number>(performance.now());
  const isFirstLoad = useRef<boolean>(true);

  useEffect(() => {
    const runAnalytics = () => {
      const now = performance.now();

      // Update analytics service context for API performance tracking
      analyticsService.setContext(location.pathname, userId);

      if (!isFirstLoad.current) {
        const timeOnPage = Math.round(now - entryTimeRef.current);
        userAnalyticsService.queuePageEvent({
          path: prevPathRef.current,
          timeOnPage,
          event: 'timeOnPage',
          timestamp: new Date().toISOString(),
          userId,
        });
      }

      userAnalyticsService.queuePageEvent({
        path: location.pathname,
        event: 'pageView',
        timestamp: new Date().toISOString(),
        userId,
      });

      prevPathRef.current = location.pathname;
      entryTimeRef.current = performance.now();
      // Reset page load start time for new page
      pageLoadStartRef.current = performance.now();
      isFirstLoad.current = false;
    };

    runAnalytics();
  }, [location, userId]);

  // Cleanup: flush any remaining events when component unmounts
  useEffect(() => {
    return () => {
      userAnalyticsService.forceFlush();
    };
  }, []);

  // Mark page as loaded and send page load duration
  const markPageLoaded = () => {
    // Delay start of idle-wait to allow API calls to initiate
    setTimeout(() => {
      analyticsService.waitForIdle().then(() => {
        const loadDuration = Math.round(performance.now() - pageLoadStartRef.current);
        // Get API performance summary for this page
        const apiSummary = analyticsService.getPageSummary();
        userAnalyticsService.queuePageEvent({
          path: location.pathname,
          loadDuration,
          event: 'loadDuration',
          timestamp: new Date().toISOString(),
          userId,
          apiMetrics: {
            totalApiRequests: apiSummary.totalRequests,
            avgApiDuration: Math.round(apiSummary.avgDuration),
            apiErrorRate: apiSummary.errorRate,
          },
        });
      });
    }, 100);
  };

  return { markPageLoaded };
};
